import { create } from 'zustand'
import type { ProjectData, SCMInfo } from '@/types'
import { getProjects, getProject, getProjectScmInfo } from '@/api/workbench'
import { getMyAccess } from '@/api/team'
import { canonicalTab } from '@/pages/workbench/simple/tabs'

export interface ProjectAccess {
  project_id: string
  owner_user_id: string
  is_owner: boolean
  roles: string[]
  permissions: string[]
}

/**
 * A Workbench tab id. The simple Workbench's tabs come from the App type's
 * journey (backend data/journeys/*.json: next, plan, screens, data, pages,
 * people, golive…) plus `advanced` and `settings`, so this is a string rather
 * than a closed union. Old ids (overview, specification, prototype, analytics,
 * support…) are redirected by pages/workbench/simple/tabs.ts.
 */
export type WorkbenchTab = string

const CURRENT_PROJECT_KEY = 'twasta_current_project'
const ACTIVE_TAB_KEY = 'twasta_active_tab'

export interface TypeProgress {
  type: string       // e.g. 'T', 'D', 'R', 'G'
  label: string      // e.g. 'Transactions', 'Dashboards', 'Reports', 'Processes'
  total: number
  specDone: number
  modelDone: number
}

interface ProjectState {
  projects: ProjectData[]
  currentProject: ProjectData | null
  activeTab: WorkbenchTab
  scmInfo: SCMInfo | null
  loading: boolean
  error: string | null
  specProgress: number | null  // 0-100 percentage of objects with specifications
  modelProgress: number | null // 0-100 percentage of objects with models generated
  typeProgress: TypeProgress[] // per-type breakdown
  projectAccess: ProjectAccess | null
  // Set by the Prototype tab while its embedded designer has unsaved changes.
  // When present, navigation away from the Prototype tab must call it with the
  // intended navigation as `proceed` (it shows a discard/save prompt) instead
  // of navigating directly. Null = nothing to guard.
  prototypeLeaveGuard: ((proceed: () => void) => void) | null

  fetchProjects: () => Promise<void>
  selectProject: (folderName: string) => Promise<void>
  restoreProject: () => Promise<void>
  setActiveTab: (tab: WorkbenchTab) => void
  fetchScmInfo: (projectId: string) => Promise<void>
  fetchProjectAccess: (projectId: string) => Promise<void>
  clearCurrentProject: () => void
  setProjects: (projects: ProjectData[]) => void
  setCurrentProject: (project: ProjectData | null) => void
  setSpecProgress: (val: number | null) => void
  setModelProgress: (val: number | null) => void
  setTypeProgress: (val: TypeProgress[]) => void
  setPrototypeLeaveGuard: (fn: ((proceed: () => void) => void) | null) => void
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  currentProject: null,
  activeTab: canonicalTab(localStorage.getItem(ACTIVE_TAB_KEY)),
  scmInfo: null,
  loading: false,
  error: null,
  specProgress: null,
  modelProgress: null,
  typeProgress: [],
  projectAccess: null,
  prototypeLeaveGuard: null,

  fetchProjects: async () => {
    set({ loading: true, error: null })
    try {
      const projects = await getProjects()
      set({ projects, loading: false })
      // After fetching projects, restore current project if none is set
      if (!get().currentProject) {
        get().restoreProject()
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load projects'
      set({ error: message, loading: false })
    }
  },

  selectProject: async (folderName: string) => {
    // Skip if already selected
    if (get().currentProject?.application.folder_name === folderName) return
    set({ loading: true, error: null, projectAccess: null })
    // Access FIRST, and independently of the project payload. `getProject`
    // needs project.read, which a run-only member (a customer organization's
    // user, invited to RUN the app) does not have — so it 403s, and if the
    // access lookup lived after it, it never ran. That left `projectAccess`
    // null, which is exactly the state `useRunOnlyRedirect` treats as "not
    // known yet": the guard could never fire for the only users it exists for,
    // and they sat on a half-loaded designer whose every call 403s. The access
    // endpoint answers for any member, so it is asked unconditionally.
    void get().fetchProjectAccess(folderName)
    try {
      const project = await getProject(folderName)
      localStorage.setItem(CURRENT_PROJECT_KEY, folderName)
      set({ currentProject: project, loading: false })
      get().fetchScmInfo(folderName)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load project'
      set({ error: message, loading: false })
    }
  },

  restoreProject: async () => {
    const saved = localStorage.getItem(CURRENT_PROJECT_KEY)
    if (!saved) return
    void get().fetchProjectAccess(saved)  // see selectProject
    try {
      const project = await getProject(saved)
      set({ currentProject: project })
      get().fetchScmInfo(saved)
    } catch {
      localStorage.removeItem(CURRENT_PROJECT_KEY)
    }
  },

  fetchProjectAccess: async (projectId: string) => {
    try {
      const res = await getMyAccess(projectId)
      const { project_id, owner_user_id, is_owner, roles, permissions } = res
      set({ projectAccess: { project_id, owner_user_id, is_owner, roles, permissions } })
    } catch {
      set({ projectAccess: null })
    }
  },

  // Idempotent, and that is what stops a blank workbench.
  //
  // WorkbenchPage's URL→store effect calls this as `urlTab !== activeTab &&
  // setActiveTab(urlTab)`, with deps [urlProject, urlTab, currentProject] —
  // `activeTab` is deliberately excluded so a TopBar click isn't reverted. That
  // means the effect can re-run (whenever `currentProject` gets a new object)
  // holding the `activeTab` its render closed over. Every one of those re-runs
  // sees the OLD value, so every one calls this again, and an unconditional
  // `set` notifies every subscriber each time — a cascade that ends in React's
  // "Maximum update depth exceeded" and a white screen.
  //
  // It only bites when the URL tab and the stored tab disagree, which is why it
  // reproduces after clearing site data (localStorage has no remembered tab, so
  // it starts at 'overview' while the URL says something else) and not on a
  // normal login, where the two already agree.
  //
  // Writing only on a real change makes those repeat calls free: the first one
  // lands, the rest are no-ops, and the cascade cannot sustain itself. The
  // effect's own guard stays as it is — this is the backstop, not a substitute.
  setActiveTab: (tab: WorkbenchTab) => {
    if (get().activeTab === tab) return
    localStorage.setItem(ACTIVE_TAB_KEY, tab)
    set({ activeTab: tab })
  },

  fetchScmInfo: async (projectId: string) => {
    try {
      const info = await getProjectScmInfo(projectId)
      set({ scmInfo: info })
    } catch {
      set({ scmInfo: null })
    }
  },

  clearCurrentProject: () => {
    localStorage.removeItem(CURRENT_PROJECT_KEY)
    localStorage.removeItem(ACTIVE_TAB_KEY)
    set({ currentProject: null, scmInfo: null, activeTab: 'next', specProgress: null, modelProgress: null, typeProgress: [], projectAccess: null })
  },

  setProjects: (projects: ProjectData[]) => {
    set({ projects })
  },

  setCurrentProject: (project: ProjectData | null) => {
    if (project) {
      localStorage.setItem(CURRENT_PROJECT_KEY, project.application.folder_name)
    } else {
      localStorage.removeItem(CURRENT_PROJECT_KEY)
    }
    set({ currentProject: project })
  },

  setSpecProgress: (val: number | null) => set({ specProgress: val }),
  setModelProgress: (val: number | null) => set({ modelProgress: val }),
  setTypeProgress: (val: TypeProgress[]) => set({ typeProgress: val }),
  // zustand treats a bare function arg to set() as an updater; wrap it so the
  // guard callback is stored as state, not invoked.
  //
  // The no-change guard is load-bearing, not tidiness. PrototypeTab registers
  // this from an effect and clears it from that effect's cleanup, so a plain
  // `set` fires on EVERY mount and unmount — usually writing the null that is
  // already there. Each write wakes every subscriber of this store, and the
  // Workbench shell is one of them: it swaps PrototypeTab for the empty state
  // whenever `currentProject` is momentarily absent. So anything that makes the
  // project flap (a failed project fetch after login is the one seen in the
  // wild) becomes mount → set → re-render → unmount → set → … until React gives
  // up with "Maximum update depth exceeded" and the screen goes blank.
  //
  // Writing only on a real change breaks that cycle at the source: a remount
  // that re-registers the same guard is silent, so a remount can no longer
  // cause the next one.
  setPrototypeLeaveGuard: (fn) => {
    if (get().prototypeLeaveGuard === fn) return
    set({ prototypeLeaveGuard: fn })
  },
}))
